import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { TenantBrandTreeSingleSelect } from '../system/TenantBrandTreeSingleSelect';
import { GenerateCompetitorsDialog } from './GenerateCompetitorsDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import { useLineOfBiz } from '../../core/contexts/LineOfBizContext';
import type {
  LineOfBizBrand,
  LineOfBizBrandAssociationType,
  TenantBrand,
  TenantOrganization,
} from '../../core/types/data-models';

/**
 * Selectable association types for the junction, with user-facing
 * labels. Mirrors `LineOfBizBrand.associationType`.
 */
const ASSOCIATION_TYPE_OPTIONS: Array<{
  value: LineOfBizBrandAssociationType;
  label: string;
}> = [
  { value: 'COMPETITOR', label: 'Competitor' },
  { value: 'OWNER', label: 'Owner' },
  { value: 'SUBSIDIARY', label: 'Subsidiary' },
  { value: 'PARTNER', label: 'Partner' },
];

/**
 * Competitor brands attached to the active LOB — junction rows where
 * `associationType = 'COMPETITOR'`. Rendered as a list of expandable
 * cards (matching the visual language of the Categories page). The
 * card header shows the brand's organization, the brand path (parent
 * → child → leaf), and a logo (default icon when the brand has no
 * `logoUrl`). Expanding reveals the brand description and a website
 * link.
 *
 * This view is read-only — attach/detach happens from the LOB
 * edit dialog so we don't replicate a second mutation surface here.
 */
export function CompetitorBrandsPage() {
  const { activeLineOfBizId } = useLineOfBiz();
  const queryClient = useQueryClient();

  // Junction rows for this LOB, filtered server-side to competitors
  // only and including the tenant-brand row so we can render
  // name/logo/description without a second round-trip per card.
  const links = useQuery({
    queryKey: [
      'line-of-biz-brands',
      { lineOfBizId: activeLineOfBizId, associationType: 'COMPETITOR' },
    ],
    queryFn: () =>
      resources.lineOfBizBrandsFor(activeLineOfBizId as number).list({
        pageSize: 500,
        sort: 'id',
        include: ['tenantBrand'],
        filter: { associationType: 'COMPETITOR' },
      }),
    enabled: activeLineOfBizId != null,
  });

  // Tenant-scoped catalogs — used to resolve organization names and to
  // walk the `parentBrandId` chain for the brand path. Shared cache
  // keys so the dashboard / picker hit warm caches when navigating
  // here.
  const orgs = useQuery({
    queryKey: ['tenant-organizations', 'select'],
    queryFn: () => resources.tenantOrganizations.list({ pageSize: 500, sort: 'name' }),
  });
  const brandsCatalog = useQuery({
    queryKey: ['tenant-brands', 'select'],
    queryFn: () => resources.tenantBrands.list({ pageSize: 1000, sort: 'name' }),
  });

  const orgById = useMemo(() => {
    const map = new Map<number, TenantOrganization>();
    for (const o of orgs.data?.data ?? []) map.set(o.id, o);
    return map;
  }, [orgs.data]);

  const brandById = useMemo(() => {
    const map = new Map<number, TenantBrand>();
    for (const b of brandsCatalog.data?.data ?? []) map.set(b.id, b);
    return map;
  }, [brandsCatalog.data]);

  /** Walk the parentBrandId chain and return ["Top", …, "Leaf"]. */
  const buildBrandPath = (brand: TenantBrand): string[] => {
    const path: string[] = [brand.name];
    const seen = new Set<number>([brand.id]);
    let cursor: TenantBrand | undefined = brand;
    while (cursor?.parentBrandId != null) {
      if (seen.has(cursor.parentBrandId)) break; // guard against cycles
      const parent = brandById.get(cursor.parentBrandId);
      if (!parent) break;
      path.unshift(parent.name);
      seen.add(parent.id);
      cursor = parent;
    }
    return path;
  };

  const rows: LineOfBizBrand[] = links.data?.data ?? [];

  // Expanded-state set follows the same pattern as the Categories page —
  // keeps focus only on rows that still exist after a refetch.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set<number>();
      for (const r of rows) if (prev.has(r.id)) next.add(r.id);
      return next;
    });
  }, [rows]);
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const expandAll = () => setExpanded(new Set(rows.map((r) => r.id)));
  const collapseAll = () => setExpanded(new Set());
  const allExpanded = rows.length > 0 && expanded.size === rows.length;

  // ----- Create / edit dialog -----------------------------------------
  // The only metadata the user can edit on the junction is the free-text
  // `associationDescription`. The brand picker is disabled in edit mode
  // because the junction's identity is the (lineOfBizId, tenantBrandId)
  // pair — we don't move associations between brands, we delete and
  // recreate.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingBrandId, setEditingBrandId] = useState<number | null>(null);
  const [editingAssociationType, setEditingAssociationType] =
    useState<LineOfBizBrandAssociationType>('COMPETITOR');
  const [editingDescription, setEditingDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LineOfBizBrand | null>(null);
  const [detachError, setDetachError] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  // Page-level mode toggle — view (browse, hides destructive +
  // create affordances) vs. edit (full controls). Defaults to view.
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  const openCreate = () => {
    setEditingRowId(null);
    setEditingBrandId(null);
    setEditingAssociationType('COMPETITOR');
    setEditingDescription('');
    setFormError(null);
    setDialogOpen(true);
  };
  const openEdit = (row: LineOfBizBrand) => {
    setEditingRowId(row.id);
    setEditingBrandId(row.tenantBrandId);
    setEditingAssociationType(row.associationType ?? 'COMPETITOR');
    setEditingDescription(row.associationDescription ?? '');
    setFormError(null);
    setDialogOpen(true);
  };

  const attach = useMutation({
    mutationFn: (data: {
      tenantBrandId: number;
      associationType: LineOfBizBrandAssociationType;
      associationDescription: string;
    }) =>
      resources.lineOfBizBrandsFor(activeLineOfBizId as number).update(
        data.tenantBrandId,
        {
          associationType: data.associationType,
          associationDescription: data.associationDescription,
        },
      ),
    onSuccess: () => {
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-brands'] });
    },
    onError: (err) => setFormError(extractErrorMessage(err)),
  });

  const detach = useMutation({
    mutationFn: (tenantBrandId: number) =>
      resources.lineOfBizBrandsFor(activeLineOfBizId as number).remove(tenantBrandId),
    onSuccess: () => {
      setConfirmDelete(null);
      setDetachError(null);
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-brands'] });
    },
    // Surface failures instead of silently leaving the dialog open — a
    // swallowed error here reads to the user as "Detach does nothing".
    onError: (err) => setDetachError(extractErrorMessage(err)),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (editingBrandId == null) {
      setFormError('Brand is required');
      return;
    }
    // Attach is idempotent on (lineOfBizId, tenantBrandId), so the same
    // call handles both create and edit of the association type +
    // description.
    await attach.mutateAsync({
      tenantBrandId: editingBrandId,
      associationType: editingAssociationType,
      associationDescription: editingDescription.trim(),
    });
  };

  if (activeLineOfBizId == null) {
    return (
      <Box>
        <PageHeader
          title="Competitor Brands"
          description="Brands tracked as competitors for the active line of business."
        />
        <Alert severity="info">
          Select a line of business from the sidebar to view its competitor
          brands.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Competitor Brands"
        description="Brands attached to this line of business with association type COMPETITOR."
        actions={
          // Toolbar mirrors the Categories page: expand/collapse-all
          // toggle, then (only in edit mode) the AI-generate + New
          // buttons, then the page-level View/Edit toggle on the right.
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all'}>
              <span>
                <IconButton
                  onClick={allExpanded ? collapseAll : expandAll}
                  disabled={rows.length === 0}
                  aria-label={allExpanded ? 'collapse all' : 'expand all'}
                >
                  {allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                </IconButton>
              </span>
            </Tooltip>
            {mode === 'edit' && (
              <>
                {/* Opens the GenerateCompetitorsDialog — kicks off the
                    LLM job, shows progress, then lets the user pick
                    which suggested competitors to attach to this LOB. */}
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setGenerateOpen(true)}
                >
                  Generate competitor brands
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                >
                  New
                </Button>
              </>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, next) => {
                if (next === 'view' || next === 'edit') setMode(next);
              }}
              aria-label="page mode"
            >
              <ToggleButton value="view" aria-label="view mode">
                <Tooltip title="View mode">
                  <VisibilityIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="edit" aria-label="edit mode">
                <Tooltip title="Edit mode">
                  <EditNoteIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
      />

      {links.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractErrorMessage(links.error)}
        </Alert>
      )}

      {detachError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDetachError(null)}>
          Couldn't detach the brand: {detachError}
        </Alert>
      )}

      {!links.isLoading && rows.length === 0 ? (
        <Alert severity="info">
          No competitor brands tracked yet. Click <strong>New</strong> to add one.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {rows.map((row) => {
            const brand = row.tenantBrand ?? brandById.get(row.tenantBrandId);
            const org =
              brand?.organizationId != null
                ? orgById.get(brand.organizationId)
                : undefined;
            const orgName = org?.name ?? null;
            const path = brand ? buildBrandPath(brand) : [`#${row.tenantBrandId}`];
            const leafName = path[path.length - 1];
            const isOpen = expanded.has(row.id);
            // Logo fallback chain: brand logo → organization logo →
            // Storefront placeholder icon. Brands often lack a logo when
            // they're a product line under a known parent org (e.g.
            // "Azure" under "Microsoft"), so falling back to the org
            // logo gives the user a recognisable visual anchor.
            const logoUrl = brand?.logoUrl || org?.logoUrl || undefined;

            return (
              <Accordion
                key={row.id}
                expanded={isOpen}
                onChange={() => toggle(row.id)}
                disableGutters
                sx={{
                  '&:before': { display: 'none' },
                  borderRadius: 1,
                  transition: (t) =>
                    t.transitions.create(['box-shadow', 'border-color']),
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ flexGrow: 1, pr: 1 }}
                  >
                    {/* Logo — uses the brand's `logoUrl` when present,
                        falls back to the parent organization's logo, and
                        finally to a Storefront icon when neither is set. */}
                    <Avatar
                      variant="rounded"
                      src={logoUrl}
                      alt={leafName}
                      sx={{ width: 40, height: 40, bgcolor: 'action.selected' }}
                    >
                      <StorefrontIcon fontSize="small" />
                    </Avatar>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      {/* Organization line — small caption with a building
                          icon so the org/brand hierarchy reads clearly. */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{ color: 'text.secondary' }}
                      >
                        <BusinessIcon fontSize="inherit" />
                        <Typography variant="caption">
                          {orgName ?? <em style={{ opacity: 0.6 }}>Unknown organization</em>}
                        </Typography>
                      </Stack>

                      {/* Brand path: "Parent / Child / Leaf" with the leaf
                          bold-primary so the eye lands on the actual brand
                          this row represents. */}
                      <Stack
                        direction="row"
                        alignItems="baseline"
                        spacing={0.5}
                        sx={{ flexWrap: 'wrap' }}
                      >
                        {path.slice(0, -1).map((segment, i) => (
                          <Box
                            key={`${segment}-${i}`}
                            component="span"
                            sx={{ display: 'inline-flex', alignItems: 'baseline' }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              {segment}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.disabled', mx: 0.5 }}
                            >
                              ›
                            </Typography>
                          </Box>
                        ))}
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{ color: 'primary.main' }}
                        >
                          {leafName}
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Junction-row id — only useful in edit mode so the
                        user can cross-reference it with a DB record. */}
                    {mode === 'edit' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        #{row.id}
                      </Typography>
                    )}

                    {/* Row actions — only visible in edit mode. Wrapped
                        in click-stoppers so opening the edit dialog or
                        delete confirm doesn't also toggle the accordion. */}
                    {mode === 'edit' && (
                      <>
                        <Tooltip title="Edit association">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(row);
                            }}
                            aria-label="edit competitor brand"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Detach">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={detach.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetachError(null);
                              setConfirmDelete(row);
                            }}
                            aria-label="detach competitor brand"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Stack spacing={1.5} sx={{ pl: 7 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Association notes
                      </Typography>
                      <Typography variant="body2">
                        {row.associationDescription || (
                          <em style={{ opacity: 0.6 }}>No notes</em>
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Brand description
                      </Typography>
                      <Typography variant="body2">
                        {brand?.description || (
                          <em style={{ opacity: 0.6 }}>No description</em>
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Website
                      </Typography>
                      <Typography variant="body2">
                        {brand?.website ? (
                          <Stack
                            component="span"
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <a
                              href={brand.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'inherit' }}
                            >
                              {brand.website}
                            </a>
                            <OpenInNewIcon
                              fontSize="inherit"
                              sx={{ color: 'text.disabled' }}
                            />
                          </Stack>
                        ) : (
                          <em style={{ opacity: 0.6 }}>Not set</em>
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      <FormDialog
        open={dialogOpen}
        title={editingRowId != null ? 'Edit competitor brand' : 'Add competitor brand'}
        submitLabel={editingRowId != null ? 'Save' : 'Add'}
        loading={attach.isPending}
        onClose={() => {
          if (attach.isPending) return;
          setDialogOpen(false);
        }}
        onSubmit={handleSubmit}
      >
        {formError && <Alert severity="error">{formError}</Alert>}
        <Stack spacing={2}>
          <TenantBrandTreeSingleSelect
            value={editingBrandId}
            onChange={setEditingBrandId}
            label="Brand"
            required
            // Brand picker stays disabled in edit mode: the junction is
            // identified by (lobId, tenantBrandId), so changing the
            // brand here would mean detach+attach against a different
            // row.
            disabled={editingRowId != null}
            helperText="Pick a brand from any organization."
          />
          <TextField
            select
            label="Association type"
            value={editingAssociationType}
            onChange={(e) =>
              setEditingAssociationType(
                e.target.value as LineOfBizBrandAssociationType,
              )
            }
            fullWidth
            helperText="How this brand relates to the line of business. This page lists COMPETITOR brands — changing it to another type moves the brand off this list."
          >
            {ASSOCIATION_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Association description"
            multiline
            minRows={2}
            fullWidth
            value={editingDescription}
            onChange={(e) => setEditingDescription(e.target.value)}
            helperText="Optional — free-text rationale for this competitor association."
          />
        </Stack>
      </FormDialog>

      <GenerateCompetitorsDialog
        open={generateOpen}
        lineOfBizId={activeLineOfBizId}
        onClose={() => setGenerateOpen(false)}
      />

      <ConfirmDialog
        open={confirmDelete != null}
        title="Detach competitor brand?"
        message={
          confirmDelete
            ? `Remove this brand from the line of business's competitor list? The brand itself is not deleted.`
            : ''
        }
        confirmLabel={detach.isPending ? 'Detaching…' : 'Detach'}
        destructive
        loading={detach.isPending}
        onConfirm={() => {
          if (confirmDelete) detach.mutate(confirmDelete.tenantBrandId);
        }}
        onClose={() => {
          if (detach.isPending) return;
          setConfirmDelete(null);
        }}
      />
    </Box>
  );
}
